import { agencyPaths, routeParts, type AgencyAccepted, type AgencyInviteAnswer, type Route } from "@nabla/api";
import { readAnswer, type Pick } from "./answers.ts";
import { getJson, sendJson, type ClientError, type Reach, type Reply } from "./http.ts";

type Role = "admin" | "gestor";
export type AgencyRead<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: ClientError };

const host: Reach = { fetch: (url, init) => fetch(url, init), api: NABLA_API, seen: () => undefined };
const commands = agencyPaths.commands;
const views = agencyPaths.views;

const reader = <T>(route: Route<"GET">, pick: Pick<T>) => async (): Promise<AgencyRead<T>> => {
  const answer = await getJson(host, routeParts(route).path);
  if (!answer.ok) return answer;
  const read = readAnswer(answer.held.body, answer.held.sameApi, pick);
  return read.ok ? read : { ok: false, error: { kind: "formato", detail: read.detail } };
};

export const agencyRead = {
  publico: reader(views.publico.route, (api) => api.agencyViews.publico.answer),
  sessao: reader(views.sessao.route, (api) => api.agencyViews.sessao.answer),
  inicio: reader(views.inicio.route, (api) => api.agencyViews.inicio.answer),
  equipe: reader(views.equipe.route, (api) => api.agencyViews.equipe.answer),
};

const key = () => crypto.randomUUID();

export const agencyCommands = {
  accept: (token: string, name: string): Promise<Reply<AgencyAccepted | null>> =>
    sendJson(host, commands.aceitarConvite.route, {}, { token, name }, (api) => api.agencyCommands.aceitarConvite.answer),
  invite: (role: Role, name: string | undefined, clients: readonly string[]): Promise<Reply<AgencyInviteAnswer | null>> => {
    const body = { role, ...(name === undefined ? {} : { name }), ...(clients.length === 0 ? {} : { clients }), requestKey: key() };
    return sendJson(host, commands.convidar.route, {}, body, (api) => api.agencyCommands.convidar.answer);
  },
  give: (member: string, client: string, role: Role) =>
    sendJson(host, commands.dar.route, {}, { member, client, role, requestKey: key() }, null),
  take: (member: string, client: string) =>
    sendJson(host, commands.tirar.route, {}, { member, client, requestKey: key() }, null),
  role: (member: string, role: Role) => sendJson(host, commands.papel.route, {}, { member, role, requestKey: key() }, null),
  signOut: () => sendJson(host, commands.sair.route, {}, {}, null),
};
